/**
 * Mobile Drawer Search with Input
 *
 * Displays a search input field connected to Algolia, showing results when
 * a query is present and recent history when the input is empty.
 */

import React, { useRef, useEffect } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { SearchResult, SearchHistoryItem } from "@west-tennessee-consulting/ui";
import { MobileDrawerSearchResults } from "./mobile-drawer-search-results";
import { MobileDrawerHistoryItems } from "./mobile-drawer-history-items";

interface MobileDrawerSearchWithTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  searchResults: SearchResult[];
  renderHighlightedText: (text: string) => React.ReactNode;
  getResultIcon: (iconType: string) => React.ReactNode;
  onResultClick: (result: SearchResult) => void;
  // History-related props
  historyItems: SearchHistoryItem[];
  onHistoryItemClick: (item: SearchHistoryItem) => void;
  onRemoveHistoryItem?: (itemId: string) => void;
  showHistoryDrawer?: boolean;
  // Full page search props
  showFullPageLink?: boolean;
  fullPageSearchUrl?: string;
  onClose?: () => void;
  // Search input props
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isSearchLoading?: boolean;
}

export function MobileDrawerSearchWithTabs({
  searchResults,
  renderHighlightedText,
  getResultIcon,
  onResultClick,
  historyItems,
  onHistoryItemClick,
  onRemoveHistoryItem,
  showFullPageLink = true,
  fullPageSearchUrl = "/search",
  onClose,
  searchQuery,
  onSearchChange,
  isSearchLoading = false,
}: MobileDrawerSearchWithTabsProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when this component mounts (search mode entered)
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const hasQuery = searchQuery.trim().length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Search input */}
      <div className="px-4 pb-3">
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search contacts, tickets, companies…"
            className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg border-0 outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-900 transition-colors"
          />
          {isSearchLoading && (
            <Loader2 className="absolute right-3 h-4 w-4 text-gray-400 animate-spin" />
          )}
          {hasQuery && !isSearchLoading && (
            <button type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-3 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Results or history */}
      <div className="flex-1 overflow-y-auto">
        {hasQuery ? (
          searchResults.length > 0 ? (
            <MobileDrawerSearchResults
              searchResults={searchResults}
              renderHighlightedText={renderHighlightedText}
              getResultIcon={getResultIcon}
              onResultClick={onResultClick}
              showFullPageLink={showFullPageLink}
              fullPageSearchUrl={fullPageSearchUrl}
              onClose={onClose}
            />
          ) : !isSearchLoading ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No results for &ldquo;{searchQuery}&rdquo;
            </div>
          ) : null
        ) : (
          <MobileDrawerHistoryItems
            historyItems={historyItems}
            onItemClick={onHistoryItemClick}
            onRemoveItem={onRemoveHistoryItem}
          />
        )}
      </div>
    </div>
  );
}
